import React, { Dispatch, SetStateAction, useState } from "react"
import dayjs from "dayjs"
import { useMutation, useQuery } from "@apollo/client"
import { Controller, SubmitHandler, useForm } from "react-hook-form"

import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { Group, InputUpdateUser, UserStatus } from "lib/types/generated/graphql-types"

import UPDATE_USER from "graphql/mutations/user/UpdateUser"
import ENABLE_USER from "graphql/mutations/user/EnableUser"
import DISABLE_USER from "graphql/mutations/user/DisableUser"
import ADD_USER_TO_GROUP from "graphql/mutations/user/AddUserToGroup"
import REMOVE_USER_FROM_GROUP from "graphql/mutations/user/RemoveUserFromGroup"
import USER from "graphql/queries/user/User"
import RESEND_PASSWORD from "graphql/mutations/user/ResendPassword"

import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import ErrorMessage from "components/Ui/Messages/ErrorMessage"
import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import Input from "components/Ui/Form/Input"
import Toggle from "components/Ui/Toggle"
import MultiSelect from "components/Ui/Form/MultiSelect"
import { ReactComponent as UserIcon } from "images/icons/user-gear.svg"
import { ReactComponent as PaperPlaneIcon } from "images/icons/paper-plane.svg"
import { AccessInfo, Label, Password, Row } from "./UpdateUser.styled"
import DELETE_USER from "../../graphql/mutations/user/DeleteUser"
import { handleErrorMessages } from "helpers/errors"
import DeleteButton from "../../components/Ui/EditSidebar/DeleteButton"
import { WithAuth } from "../../components/Auth"
import { PermissionGroups } from "../../routes/constants"
import { useUserGroups } from "../../hooks/useUserGroups"
import FallbackMultiselect from "../../components/Ui/Form/FallbackMultiselect"
import FallbackInput from "../../components/Ui/Form/FallbackInput"

type Props = {
  editUser: string
  setEditUser: Dispatch<SetStateAction<string>>
  userEmail: string
  refetch: () => void
  groupList: Group[]
}

export const UpdateUser = ({ editUser, setEditUser, userEmail, refetch, groupList }: Props) => {
  const dispatch = useAppDispatch()
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<InputUpdateUser>()
  const [isEnabled, setIsEnabled] = useState<boolean>()
  const [addedGroups, setAddedGroups] = useState<string[]>([])
  const [passwordButtonDisabled, setPasswordButtonDisabled] = useState(false)

  const { hasAnyGroup } = useUserGroups()

  const successAlert = (message: string) =>
    dispatch(
      alertActions.actions.createAlert({
        message: message,
        type: "success"
      })
    )

  const { loading, error, data } = useQuery(USER, {
    variables: { username: editUser },
    onCompleted: (data) => {
      setIsEnabled(data.user.enabled)
      setAddedGroups(
        data.user.groups.map((group: Group) => group.name)
      )
    }
  })

  const [resendPassword, { loading: passwordLoading }] = useMutation(RESEND_PASSWORD, {
    variables: { username: editUser },
    onCompleted: () => {
      successAlert(`New password sent to ${userEmail}`)
      setPasswordButtonDisabled(true)
    },
    onError: (error) => handleErrorMessages(error)
  })

  const [enableUser] = useMutation(ENABLE_USER, {
    variables: { username: editUser },
    onCompleted: () => {
      successAlert(`User ${userEmail} successfully enabled`)
      refetch()
    },
    onError: (error) => handleErrorMessages(error)
  })

  const [disableUser] = useMutation(DISABLE_USER, {
    variables: { username: editUser },
    onCompleted: () => {
      successAlert(`User ${userEmail} successfully disabled`)
      refetch()
    },
    onError: (error) => handleErrorMessages(error)
  })

  const [addUserToGroup] = useMutation(ADD_USER_TO_GROUP, {
    onCompleted: () => successAlert(`User ${userEmail} successfully added to group`),
    onError: (error) => handleErrorMessages(error)
  })

  const [removeUserFromGroup] = useMutation(REMOVE_USER_FROM_GROUP, {
    onCompleted: () => successAlert(`User ${userEmail} successfully removed from group`),
    onError: (error) => handleErrorMessages(error)
  })

  const handleEnableUser = (value: boolean) => {
    setIsEnabled(value)
    if (value) {
      enableUser()
      return
    }
    disableUser()
  }

  const updateGroups = (value: string[]) => {
    const addedGroup = value.filter((x) => !addedGroups.includes(x))
    const removedGroup = addedGroups.filter((x) => !value.includes(x))
    if (addedGroup.length > 0) {
      addUserToGroup({
        variables: { username: editUser, groupName: addedGroup[0] }
      })
    }

    if (removedGroup.length > 0) {
      removeUserFromGroup({
        variables: { username: editUser, groupName: removedGroup[0] }
      })
    }
    setAddedGroups(value)
  }

  const closeSidebar = () => {
    dispatch(hideEditSidebar())
    setPasswordButtonDisabled(false)
    setEditUser("")
  }

  const [updateUser, { loading: createLoading }] = useMutation(UPDATE_USER, {
    onCompleted: () =>  {
      successAlert(`User ${userEmail} successfully updated`)
      refetch()
    },
    onError: (error) => handleErrorMessages(error)
  })

  const onDeleted = () => {
    refetch()
    closeSidebar()
  }

  const onSubmit: SubmitHandler<InputUpdateUser> = (data) => {
    updateUser({
      variables: {
        ...data,
        username: editUser
      }
    })
  }

  if (loading) return <EditSidebar loading={true} />
  if (error)
    return (
      <ErrorMessage
        showRefreshButton
        message={
          <>
            Error loading User. Try refreshing the page, or contact{" "}
            <a href="mailto:support@brinkcommerce.com">support</a>. Error loading User. Try
            refreshing the page, or contact <a href="mailto:support@brinkcommerce.com">support</a>.
          </>
        }
      />
    )

  const { email, givenName, familyName, created, updated, status, groups } = data.user

  return (
    <EditSidebar setEditId={setEditUser} cancelEvent={closeSidebar}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <EditSidebarHeader
          title={"Merchant Portal user"}
          setEditId={setEditUser}
          id={email}
          icon={<UserIcon />}
        >
          <WithAuth requiredGroups={PermissionGroups.users.write}>
            <DeleteButton
              id={editUser}
              name={editUser}
              entity={"user"}
              mutation={DELETE_USER}
              mutationIdentifier="username"
              secureDelete={true}
              postDeleteFunction={onDeleted}
            />
            <PrimaryButton type="submit" loading={createLoading}>
              Save
            </PrimaryButton>
          </WithAuth>
        </EditSidebarHeader>
        <Row>
          <Label>Enabled:</Label>
          <Toggle
            value={isEnabled}
            setValue={hasAnyGroup(PermissionGroups.users.write) ? handleEnableUser : () => {}}
          />
        </Row>
        <Row>
          <Label>Created:</Label>
          <div>{dayjs(created).format("YYYY-MM-DD")}</div>
        </Row>
        <Row>
          <Label>Updated:</Label>
          <div>{dayjs(updated).format("YYYY-MM-DD")}</div>
        </Row>
        <Row>
          <Label>Status:</Label>
          <div>{status === UserStatus.ExternalProvider ? UserStatus.Confirmed : status}</div>
        </Row>
        <Row>
          <Label>Source:</Label>
          <div>
            {status === UserStatus.ExternalProvider
              ? "External Identity Provider"
              : "BrinkCommerce"}
          </div>
        </Row>
        {status === "FORCE_CHANGE_PASSWORD" && (
          <WithAuth requiredGroups={PermissionGroups.users.write}>
            <Password>
              <PrimaryButton
                type="button"
                handleClick={() => resendPassword()}
                loading={passwordLoading}
                disabled={passwordButtonDisabled}
              >
                <PaperPlaneIcon />
                Resend password
              </PrimaryButton>
            </Password>
          </WithAuth>
        )}
        <hr />
        <WithAuth
          requiredGroups={PermissionGroups.users.write}
          fallback={
            <FallbackMultiselect
              label={"Access"}
              values={groups.map((group: Group) => group.name)}
            />
          }
        >
          <MultiSelect
            label="Access"
            setValue={updateGroups}
            returnValueOnly={true}
            defaultValue={groups.map((g: Group) => ({
              value: g.name,
              label: g.name
            }))}
            options={groupList.map((g: Group) => ({
              value: g.name,
              label: g.name
            }))}
          />
        </WithAuth>
        {addedGroups.map((groupName) => (
          <AccessInfo key={groupName}>
            <Label>{groupName}</Label>
            <div>{groupList?.find((g) => g.name === groupName)?.description}</div>
          </AccessInfo>
        ))}
        <hr />
        <WithAuth
          requiredGroups={PermissionGroups.users.write}
          fallback={<FallbackInput label={"Given name"} value={givenName} />}
        >
          <Controller
            name="givenName"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label="Given name *"
                placeholder="Given name"
                errors={errors}
              />
            )}
            defaultValue={givenName}
            control={control}
            rules={{
              required: "This is a required field"
            }}
          />
        </WithAuth>
        <WithAuth
          requiredGroups={PermissionGroups.users.write}
          fallback={<FallbackInput label={"Family name"} value={familyName} />}
        >
          <Controller
            name="familyName"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label="Family name *"
                placeholder="Family name"
                errors={errors}
              />
            )}
            defaultValue={familyName}
            control={control}
            rules={{
              required: "This is a required field"
            }}
          />
        </WithAuth>
      </form>
    </EditSidebar>
  )
}
